import React, { useState } from 'react';
import '../../../styles/Auction.css';
import { useSocket } from '../../../context/SocketContext';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../common/Button';

const BidPanel = ({ sessionId, currentHighest, minIncrement = 1 }) => {
    const { socketService } = useSocket();
    const { user } = useAuth();

    // Hooks must come BEFORE any return
    const [bidAmount, setBidAmount] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isManager = user?.role === 'manager' || user?.is_manager === true;

    // Managers should not see bidding UI
    if (isManager) {
        return null;
    }

    const minBid = currentHighest
        ? parseFloat(currentHighest) + minIncrement
        : minIncrement;

    const handleQuickIncrement = (increment) => {
        const base = bidAmount ? parseFloat(bidAmount) : minBid;
        const newBid = Math.max(minBid, base + increment);
        setBidAmount(newBid.toString());
        setError('');
    };

    const handlePlaceBid = (e) => {
        e.preventDefault();
        const amount = parseFloat(bidAmount);

        if (isNaN(amount)) {
            setError('Please enter a valid number.');
            return;
        }

        if (amount < minBid) {
            setError(`Bid must be at least $${minBid.toLocaleString()}`);
            return;
        }

        if (!socketService || !socketService.isConnected) {
            setError('Not connected to auction server.');
            return;
        }

        setIsSubmitting(true);
        const success = socketService.placeBid(sessionId, amount);
        setIsSubmitting(false);

        if (success) {
            setBidAmount('');
            setError('');
        } else {
            setError('Failed to place bid. Please try again.');
        }
    };

    return (
        <div className="bid-panel">
            <h3>Place a Bid</h3>
            <form onSubmit={handlePlaceBid}>
                <div>
                    <label>Your Bid (Min: <span className="tabular-nums font-mono">${minBid.toLocaleString()}</span>)</label>
                    <div className="bid-input-group">
                        <span className="currency-symbol">$</span>
                        <input
                            type="number"
                            value={bidAmount}
                            onChange={(e) => {
                                setBidAmount(e.target.value);
                                setError('');
                            }}
                            min={minBid}
                            step="1000"
                            placeholder={minBid.toString()}
                        />
                    </div>

                    {/* Quick increment chips */}
                    <div className="quick-bid-chips">
                        <button type="button" className="quick-bid-chip" onClick={() => handleQuickIncrement(100)}>
                            +$100
                        </button>
                        <button type="button" className="quick-bid-chip" onClick={() => handleQuickIncrement(500)}>
                            +$500
                        </button>
                        <button type="button" className="quick-bid-chip" onClick={() => handleQuickIncrement(1000)}>
                            +$1,000
                        </button>
                        <button type="button" className="quick-bid-chip" onClick={() => handleQuickIncrement(5000)}>
                            +$5,000
                        </button>
                    </div>
                </div>

                {error && <p className="error-message">{error}</p>}

                <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    loading={isSubmitting}
                    disabled={!bidAmount}
                    style={{ width: '100%', marginTop: '0.5rem' }}
                >
                    Place Bid
                </Button>
            </form>
        </div>
    );
};

export default BidPanel;
